import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  ErrorOutline as ErrorOutlineIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from "@mui/icons-material";
import { STRIPE_CONNECT } from "../PaymentSetup.constants";
import {
  CardHeader,
  CardTitleRow,
  PrimaryButton,
  ChipContainer,
} from "../PaymentSetup.styles";

const FALLBACK_COUNTRIES = [{ code: "US", name: "United States" }];

const StripeConnectCard = ({
  accountStatus,
  actionLoading,
  supportedCountries = [],
  onRefresh,
  onCreateAccount,
  onContinueOnboarding,
  onOpenDashboard,
}) => {
  const [selectedCountry, setSelectedCountry] = useState("US");
  const countries =
    supportedCountries.length > 0 ? supportedCountries : FALLBACK_COUNTRIES;
  const getContinueButtonLabel = () => {
    if (actionLoading) return STRIPE_CONNECT.BUTTON_LOADING;
    if (accountStatus?.verificationErrors?.length > 0)
      return STRIPE_CONNECT.BUTTON_UPDATE_INFO;
    if (accountStatus?.detailsSubmitted)
      return STRIPE_CONNECT.BUTTON_REVIEW_STRIPE;
    return STRIPE_CONNECT.BUTTON_CONTINUE_STRIPE;
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <CardHeader>
          <CardTitleRow>
            <AccountBalanceIcon color="primary" />
            <Typography variant="h6">{STRIPE_CONNECT.CARD_TITLE}</Typography>
          </CardTitleRow>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={actionLoading}
          >
            {STRIPE_CONNECT.REFRESH_BUTTON}
          </Button>
        </CardHeader>

        {!accountStatus?.hasAccount ? (
          <Box>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {STRIPE_CONNECT.NO_ACCOUNT_INTRO}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {STRIPE_CONNECT.NO_ACCOUNT_DURATION}
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }} size="small">
              <InputLabel id="stripe-country-label">
                {STRIPE_CONNECT.COUNTRY_SELECT_LABEL}
              </InputLabel>
              <Select
                labelId="stripe-country-label"
                id="stripe-country-select"
                value={selectedCountry}
                label={STRIPE_CONNECT.COUNTRY_SELECT_LABEL}
                onChange={(e) => setSelectedCountry(e.target.value)}
                disabled={actionLoading}
              >
                {countries.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {STRIPE_CONNECT.COUNTRY_SELECT_HELPER}
              </FormHelperText>
            </FormControl>

            <PrimaryButton
              variant="contained"
              onClick={() => onCreateAccount(selectedCountry)}
              disabled={actionLoading || !selectedCountry}
              startIcon={
                actionLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <AccountBalanceIcon />
                )
              }
            >
              {actionLoading
                ? STRIPE_CONNECT.SETTING_UP
                : STRIPE_CONNECT.CONNECT_BUTTON}
            </PrimaryButton>
          </Box>
        ) : !accountStatus?.onboardingComplete ? (
          <Box>
            {accountStatus?.detailsSubmitted ? (
              <>
                {accountStatus?.verificationErrors?.length > 0 ? (
                  <>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Chip
                        icon={<ErrorOutlineIcon />}
                        label={STRIPE_CONNECT.CHIP_VERIFICATION_FAILED}
                        color="error"
                        variant="outlined"
                      />
                    </Box>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      gutterBottom
                    >
                      {STRIPE_CONNECT.VERIFICATION_FAILED_INTRO}
                    </Typography>
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {STRIPE_CONNECT.VERIFICATION_ISSUES_LABEL}
                      </Typography>
                      {accountStatus.verificationErrors.map((err, idx) => (
                        <Typography key={idx} variant="body2">
                          - {err.reason || err.code}
                          {err.requirement && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 0.5 }}
                            >
                              ({err.requirement})
                            </Typography>
                          )}
                        </Typography>
                      ))}
                    </Alert>
                    {window.location.hostname !== "mynirva.com" && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {STRIPE_CONNECT.VERIFICATION_TIP_TEST_MODE}
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Chip
                        icon={<HourglassEmptyIcon />}
                        label={STRIPE_CONNECT.CHIP_AWAITING_VERIFICATION}
                        color="info"
                        variant="outlined"
                      />
                    </Box>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      gutterBottom
                    >
                      {STRIPE_CONNECT.AWAITING_VERIFICATION_INTRO}
                    </Typography>
                  </>
                )}
              </>
            ) : (
              <>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Chip
                    icon={<WarningIcon />}
                    label={STRIPE_CONNECT.CHIP_ONBOARDING_INCOMPLETE}
                    color="warning"
                    variant="outlined"
                  />
                </Box>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  {STRIPE_CONNECT.INCOMPLETE_INTRO}
                </Typography>
              </>
            )}

            {accountStatus?.requirements?.currently_due?.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {STRIPE_CONNECT.REMAINING_REQUIREMENTS}{" "}
                {accountStatus.requirements.currently_due.join(", ")}
              </Typography>
            )}

            <PrimaryButton
              variant="contained"
              onClick={onContinueOnboarding}
              disabled={actionLoading}
              startIcon={
                actionLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <OpenInNewIcon />
                )
              }
            >
              {getContinueButtonLabel()}
            </PrimaryButton>
          </Box>
        ) : (
          <Box>
            <ChipContainer>
              <Chip
                icon={<CheckCircleIcon />}
                label={STRIPE_CONNECT.CHIP_CONNECTED}
                color="success"
                variant="outlined"
              />
              {accountStatus.chargesEnabled && (
                <Chip
                  label={STRIPE_CONNECT.CHIP_CHARGES_ENABLED}
                  color="success"
                />
              )}
              {accountStatus.payoutsEnabled && (
                <Chip
                  label={STRIPE_CONNECT.CHIP_PAYOUTS_ENABLED}
                  color="success"
                />
              )}
            </ChipContainer>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {STRIPE_CONNECT.FULLY_ONBOARDED_INTRO}
            </Typography>
            <PrimaryButton
              variant="outlined"
              onClick={onOpenDashboard}
              disabled={actionLoading}
              startIcon={<OpenInNewIcon />}
              sx={{ mt: 1 }}
            >
              {STRIPE_CONNECT.OPEN_DASHBOARD}
            </PrimaryButton>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default StripeConnectCard;
